import { useState } from 'react';
import { LanguagePicker, useSession, useT } from '@adiox/ui';
import { prefsStore, shellOf, usePrefsValue } from '@adiox/ui';
import { usePrefs } from '@adiox/ui';
import { THEMES, type Density, type ThemeId } from '@adiox/ui';
import { ChangePassword, Modal } from '@adiox/ui';
import {
  NUMBER_FORMATS,
  numberFormatOf,
  serverOffsetLabel,
  timeZoneOf,
  useServerOffset,
  type NumberFormat,
  type TimeZoneMode,
} from '@adiox/ui';
import { useActiveLogin } from '../trading/useTrading';
import { playTone } from '../notifications/useNotificationEffects';
import { accountName, useAccountLabels } from './useAccountLabels';
import { InvestorPasswordSection } from './InvestorPasswordSection';
import { DemoAccountSection, EmailNotificationsSection } from './AccountExtras';

const field =
  'w-full rounded-md border border-line bg-panel2 px-2 py-1 text-sm outline-none focus:border-accent';
const label = 'mb-1 block text-xs uppercase tracking-wide text-muted';

/** Appearance, notifications, trading confirmations and the account password. Everything but the password is a synced preference. */
export function SettingsDialog({
  open,
  onClose,
  onOpenShortcuts,
}: {
  open: boolean;
  onClose: () => void;
  onOpenShortcuts: () => void;
}) {
  const user = useSession().user;
  const t = useT();
  const readOnly = user?.readOnly === true;
  const login = useActiveLogin();
  const labels = useAccountLabels();
  const { themeChoice, density, setTheme, setDensity } = usePrefs();
  const timeZone = usePrefsValue((s) => timeZoneOf(s.data));
  const numberFormat = usePrefsValue((s) => numberFormatOf(s.data));
  const tabTitle = usePrefsValue((s) => s.data.tabTitle ?? 'symbol');
  const shell = usePrefsValue((s) => shellOf(s.data));
  const serverOffset = useServerOffset();
  const toasts = usePrefsValue((s) => s.data.notifyToasts !== false);
  const sound = usePrefsValue((s) => s.data.notifySound ?? 'alerts');
  const desktop = usePrefsValue((s) => s.data.notifyDesktop === true);
  const [permission, setPermission] = useState(() =>
    typeof Notification === 'undefined' ? 'unsupported' : Notification.permission,
  );
  const acknowledged = usePrefsValue((s) => s.data.oneClickAck === true);
  const confirmMarket = usePrefsValue((s) => s.data.confirmMarketOrders === true);
  const confirmClose = usePrefsValue((s) => s.data.confirmClose === true);

  return (
    <Modal open={open} title={t('Settings')} onClose={onClose} size="md">
      <div className="flex max-h-[70vh] flex-col gap-5 overflow-auto pr-1">
        <section aria-label="Appearance" className="flex flex-col gap-2">
          <h3 className="text-sm font-semibold">{t('Appearance')}</h3>
          <LanguagePicker />
          <div>
            <label className={label} htmlFor="set-theme">
              Theme
            </label>
            <select
              id="set-theme"
              className={field}
              value={themeChoice}
              onChange={(e) => setTheme(e.target.value as ThemeId | 'system')}
            >
              <option value="system">Follow system (light or dark)</option>
              {(Object.keys(THEMES) as ThemeId[]).map((id) => (
                <option key={id} value={id}>
                  {THEMES[id].label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className={label} htmlFor="set-shell">
              Workspace (desktop)
            </label>
            <select
              id="set-shell"
              className={field}
              value={shell}
              onChange={(e) => prefsStore.set('shell', e.target.value as 'classic' | 'modern')}
            >
              <option value="classic">Classic terminal (menu bar, toolbar, Navigator, Toolbox)</option>
              <option value="modern">Adiox (top bar and panels)</option>
            </select>
            <p className="mt-1 text-xs text-muted">
              Changing it sets the window layout back to that workspace's own.
            </p>
          </div>
          <div>
            <label className={label} htmlFor="set-density">
              Density
            </label>
            <select
              id="set-density"
              className={field}
              value={density}
              onChange={(e) => setDensity(e.target.value as Density)}
            >
              <option value="comfortable">Comfortable (spacious)</option>
              <option value="compact">Compact (more on screen)</option>
            </select>
          </div>
          <div>
            <label className={label} htmlFor="set-tab-title">
              Browser tab shows
            </label>
            <select
              id="set-tab-title"
              className={field}
              value={tabTitle}
              onChange={(e) => prefsStore.set('tabTitle', e.target.value as 'symbol' | 'profit' | 'off')}
            >
              <option value="symbol">The symbol and its price</option>
              <option value="profit">The floating profit</option>
              <option value="off">Just the name</option>
            </select>
          </div>
        </section>

        <section aria-label="Time and numbers" className="flex flex-col gap-2">
          <h3 className="text-sm font-semibold">{t('Time and numbers')}</h3>
          <div>
            <label className={label} htmlFor="set-time-zone">
              Show times in
            </label>
            <select
              id="set-time-zone"
              className={field}
              value={timeZone}
              onChange={(e) => prefsStore.set('timeZone', e.target.value as TimeZoneMode)}
            >
              <option value="server">
                Server time{serverOffset === null ? '' : ` (${serverOffsetLabel(serverOffset)})`}
              </option>
              <option value="local">This device's time</option>
              <option value="utc">UTC</option>
            </select>
            <p className="mt-1 text-xs text-muted">
              Used by the trade list, history, notifications, alerts and order expiry. Charts and the clock in
              the top bar always use server time, like the trading day.
            </p>
          </div>
          <div>
            <label className={label} htmlFor="set-number-format">
              Amounts of money
            </label>
            <select
              id="set-number-format"
              className={field}
              value={numberFormat}
              onChange={(e) => prefsStore.set('numberFormat', e.target.value as NumberFormat)}
            >
              {NUMBER_FORMATS.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.label}
                </option>
              ))}
            </select>
          </div>
        </section>

        <section aria-label="Notifications" className="flex flex-col gap-2">
          <h3 className="text-sm font-semibold">{t('Notifications')}</h3>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={toasts}
              onChange={(e) => prefsStore.set('notifyToasts', e.target.checked)}
              className="accent-[var(--accent)]"
            />
            Show pop-up notifications (the notification log always records them)
          </label>
          <div>
            <label className={label} htmlFor="set-sound">
              Sounds
            </label>
            <div className="flex gap-2">
              <select
                id="set-sound"
                className={field}
                value={sound}
                onChange={(e) => prefsStore.set('notifySound', e.target.value as 'off' | 'alerts' | 'all')}
              >
                <option value="alerts">For warnings and alerts (margin call, stop out, price alerts)</option>
                <option value="all">
                  For every trade event (a different sound for opens, profits, losses, stops, orders, alerts
                  and messages)
                </option>
                <option value="off">Off</option>
              </select>
              <button
                type="button"
                onClick={() => playTone('danger')}
                className="whitespace-nowrap rounded border border-line px-3 py-1 text-sm hover:border-accent"
              >
                Test
              </button>
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={desktop && permission === 'granted'}
              disabled={permission === 'unsupported' || permission === 'denied'}
              onChange={async (e) => {
                if (!e.target.checked) return prefsStore.set('notifyDesktop', false);
                // asked only now, when the user turns it on
                const answer = permission === 'granted' ? 'granted' : await Notification.requestPermission();
                setPermission(answer);
                prefsStore.set('notifyDesktop', answer === 'granted');
              }}
              className="accent-[var(--accent)]"
            />
            Desktop notifications when the terminal is in a background tab
          </label>
          {permission === 'denied' && (
            <p className="text-xs text-warn">
              This browser blocks notifications from the terminal. Allow them in the site settings (the icon
              left of the address), then turn this on.
            </p>
          )}
          {permission === 'unsupported' && (
            <p className="text-xs text-muted">This browser cannot show desktop notifications.</p>
          )}
        </section>

        <section aria-label="Trading" className="flex flex-col gap-2">
          <h3 className="text-sm font-semibold">{t('Trading')}</h3>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={confirmMarket}
              onChange={(e) => prefsStore.set('confirmMarketOrders', e.target.checked)}
              className="accent-[var(--accent)]"
            />
            Confirm market orders from the order ticket before sending
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={confirmClose}
              onChange={(e) => prefsStore.set('confirmClose', e.target.checked)}
              className="accent-[var(--accent)]"
            />
            Confirm before closing a single position
          </label>
          <p className="text-sm text-muted">
            One-click trading is off on every chart until you turn it on, and you confirm the first time.
          </p>
          <button
            type="button"
            disabled={!acknowledged}
            onClick={() => prefsStore.set('oneClickAck', false)}
            className="self-start rounded border border-line px-3 py-1 text-sm hover:border-accent disabled:opacity-40"
          >
            Ask for confirmation again
          </button>
        </section>

        <section aria-label="Account" className="flex flex-col gap-2">
          <h3 className="text-sm font-semibold">{t('Account')}</h3>
          <p className="text-sm text-muted" data-testid="signed-in-as">
            Signed in with account{' '}
            <span className="text-text">
              {login === undefined ? '—' : accountName(login, labels.get(login))}
            </span>
            {user?.email && (
              <>
                {' '}
                · email on file <span className="text-text">{user.email}</span>
              </>
            )}
          </p>
          <button
            type="button"
            onClick={onOpenShortcuts}
            className="self-start rounded border border-line px-3 py-1 text-sm hover:border-accent"
          >
            Keyboard shortcuts…
          </button>
          <EmailNotificationsSection />
          <DemoAccountSection />
          {open && !readOnly && <ChangePassword />}
          {open && <InvestorPasswordSection />}
        </section>
      </div>
      <div className="mt-4 flex justify-end">
        <button
          type="button"
          onClick={onClose}
          className="rounded-md border border-line px-4 py-1.5 text-sm hover:border-accent"
        >
          Done
        </button>
      </div>
    </Modal>
  );
}
